import { useState, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import addDays from 'date-fns/addDays';
import setHours from 'date-fns/setHours';
import setMinutes from 'date-fns/setMinutes';

import FormControl from 'components/UI/Inputs/FormControl';
import InputLabel from 'components/UI/Inputs/InputLabel';
import Select from 'components/UI/Inputs/Select';
import { useFormat } from 'src/lib/date-fns';

import DateTimePicker from 'src/features/tasks/TaskForm/GenericForm/DateTimeSelect/DateTimePicker';

export default function DueDateSelect({
  label,
  value,
  onChange,
  defaultPreset = '30_days',
}: {
  label: string;
  value: Date | null;
  onChange: (nextValue: Date | null) => void;
  defaultPreset?: 'none' | '15_days' | '30_days';
}) {
  const anchorRef = useRef();
  const { t } = useTranslation('proforma-invoices-page');
  const format = useFormat();

  const [today] = useState(new Date());

  const in15Days = setMinutes(setHours(addDays(today, 15), 23), 59);
  const in30Days = setMinutes(setHours(addDays(today, 30), 23), 59);

  const [initialized, setInitialized] = useState(false);

  // Set default preset on first render if value is null
  if (!initialized) {
    setInitialized(true);
    if (value === null && defaultPreset !== 'none') {
      const presetDate = defaultPreset === '15_days' ? in15Days : in30Days;
      setTimeout(() => onChange(presetDate), 0);
    }
  }

  const [pickerOpen, setPickerOpen] = useState(false);

  const isPresetValue = (val: Date | null): boolean => {
    if (!val) return true;
    const valTime = val.getTime();
    return valTime === in15Days.getTime() || valTime === in30Days.getTime();
  };

  const customValue = value && !isPresetValue(value) ? value.getTime() : null;

  const id = `proforma_invoice_form_${label}`;

  return (
    <div ref={anchorRef}>
      <FormControl fullWidth>
        <InputLabel htmlFor={id}>{label}</InputLabel>
        <Select
          variant="outlined"
          value={value === null ? 0 : value.getTime()}
          onChange={(event) => {
            const selectedValue =
              (event.target.value as number) === 0 ? null : new Date(event.target.value as number);
            onChange(selectedValue);
          }}
          inputProps={{ id: id }}
          options={[
            { value: 0, text: t('form.date_time_select.value.none') },
            {
              value: in15Days.getTime(),
              text: t('form.date_time_select.value.in_15_days', {
                date: format(in15Days, 'd MMM'),
              }),
            },
            {
              value: in30Days.getTime(),
              text: t('form.date_time_select.value.in_30_days', {
                date: format(in30Days, 'd MMM'),
              }),
            },
            {
              value: customValue,
              text: customValue
                ? format(new Date(customValue), 'd MMM yyyy')
                : t('form.date_time_select.value.custom'),
              onClick: () => setPickerOpen(true),
            },
          ]}
        />
      </FormControl>

      <DateTimePicker
        open={pickerOpen}
        anchorEl={anchorRef.current}
        onSave={(date) => {
          onChange(date);
          setPickerOpen(false);
        }}
        onClose={() => setPickerOpen(false)}
      />
    </div>
  );
}
